import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { AngularAccent } from './GeometricPatterns';

const VEHICLE_FILTERS = [
  { id: 'all', label: 'Alle', icon: 'Car' },
  { id: 'ev', label: 'Elektrisk', icon: 'Zap' },
  { id: 'hybrid', label: 'Hybrid', icon: 'Leaf' },
  { id: 'standard', label: 'Standard', icon: 'Truck' },
];

const DATE_FILTERS = [
  { id: 'all', label: 'Alle' },
  { id: '7d', label: '7 dager' },
  { id: '30d', label: '30 dager' },
  { id: '90d', label: '3 måneder' },
];

const FilterControls = ({ onFilterChange, totalTrips = 0 }) => {
  const [activeVehicle, setActiveVehicle] = useState('all');
  const [activePeriod, setActivePeriod] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');

  const handleVehicleFilter = (id) => {
    setActiveVehicle(id);
    onFilterChange?.({ vehicle: id, period: activePeriod, sort: sortOrder });
  };

  const handlePeriodFilter = (id) => {
    setActivePeriod(id);
    onFilterChange?.({ vehicle: activeVehicle, period: id, sort: sortOrder });
  };

  const toggleSort = () => {
    const next = sortOrder === 'newest' ? 'oldest' : 'newest';
    setSortOrder(next);
    onFilterChange?.({ vehicle: activeVehicle, period: activePeriod, sort: next });
  };

  return (
    <div
      className="relative overflow-hidden"
      style={{
        background: 'var(--color-card)',
        border: '2px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: '20px 24px',
      }}
    >
      <AngularAccent className="top-0 right-0" width={50} height={50} color="#004F98" />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div
            style={{
              width: 32,
              height: 32,
              background: 'rgba(255,87,34,0.12)',
              border: '1.5px solid rgba(255,87,34,0.3)',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="Filter" size={16} color="#FF5722" />
          </div>
          <span
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 600,
              fontSize: '14px',
              color: 'var(--color-foreground)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            Filtrer reiser
          </span>
          <span
            style={{
              fontFamily: 'var(--font-caption)',
              fontSize: '12px',
              color: 'var(--color-muted-foreground)',
              background: 'rgba(156,163,175,0.1)',
              border: '1px solid var(--color-border)',
              borderRadius: '2px',
              padding: '2px 8px',
            }}
          >
            {totalTrips} reiser
          </span>
        </div>

        <button
          onClick={toggleSort}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 14px',
            background: 'rgba(0,79,152,0.08)',
            border: '1.5px solid rgba(0,79,152,0.25)',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'var(--font-caption)',
            fontSize: '12px',
            fontWeight: 500,
            color: 'var(--color-primary)',
            cursor: 'pointer',
            transition: 'all 150ms ease-out',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          <Icon name={sortOrder === 'newest' ? 'ArrowDown' : 'ArrowUp'} size={14} />
          {sortOrder === 'newest' ? 'Nyeste først' : 'Eldste først'}
        </button>
      </div>
      {/* Vehicle type filter */}
      <div className="mb-4">
        <div
          style={{
            fontFamily: 'var(--font-caption)',
            fontSize: '11px',
            fontWeight: 600,
            color: 'var(--color-muted-foreground)',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: '8px',
          }}
        >
          Kjøretøytype
        </div>
        <div className="flex flex-wrap gap-2">
          {VEHICLE_FILTERS?.map((f) => (
            <button
              key={f?.id}
              onClick={() => handleVehicleFilter(f?.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 14px',
                background: activeVehicle === f?.id ? 'var(--color-primary)' : 'rgba(156,163,175,0.06)',
                border: activeVehicle === f?.id
                  ? '1.5px solid var(--color-primary)'
                  : '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-caption)',
                fontSize: '13px',
                fontWeight: activeVehicle === f?.id ? 600 : 400,
                color: activeVehicle === f?.id ? '#fff' : 'var(--color-muted-foreground)',
                cursor: 'pointer',
                transition: 'all 150ms ease-out',
                boxShadow: activeVehicle === f?.id
                  ? '0 2px 8px rgba(0,79,152,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
                  : 'inset 0 1px 0 rgba(255,255,255,0.03)',
              }}
            >
              <Icon name={f?.icon} size={13} />
              {f?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Period filter */}
      <div>
        <div
          style={{
            fontFamily: 'var(--font-caption)',
            fontSize: '11px',
            fontWeight: 600,
            color: 'var(--color-muted-foreground)',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: '8px',
          }}
        >
          Tidsperiode
        </div>
        <div className="flex flex-wrap gap-2">
          {DATE_FILTERS?.map((f) => (
            <button
              key={f?.id}
              onClick={() => handlePeriodFilter(f?.id)}
              style={{
                padding: '5px 14px',
                background: activePeriod === f?.id ? 'rgba(255,87,34,0.15)' : 'rgba(156,163,175,0.06)',
                border: activePeriod === f?.id
                  ? '1.5px solid rgba(255,87,34,0.5)'
                  : '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-caption)',
                fontSize: '13px',
                fontWeight: activePeriod === f?.id ? 600 : 400,
                color: activePeriod === f?.id ? '#FF5722' : 'var(--color-muted-foreground)',
                cursor: 'pointer',
                transition: 'all 150ms ease-out',
              }}
            >
              {f?.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FilterControls;
